import React, {useState, useEffect, Dispatch, SetStateAction} from 'react'
import {useNavigate} from 'react-router-dom'
import {ID, KTIcon} from '@/_metronic/helpers'
import { Formik, FieldArray, Field, Form } from 'formik';
import {
  Supplier,
  RegistredProduct,
  OpProduct,
  SupplierOperation
} from '../../core/_models'
import {
  getSuppliersArray,
  getRegistredProducts,
  createBulkOrder,
  updateBulkOrder,
} from '../../core/_requests'
import {CustomSelect, InputText, FormActions} from '@/components/form'
import Lookup from './Lookup'
import Subtotal from './Subtotal'
import * as Yup from 'yup';

const initialValues = {
  supplier: '',
  products: [] as OpProduct[],
};

const validationSchema = Yup.object().shape({
  products: Yup.array().of(
    Yup.object().shape({
      product: Yup.string().required(''),
      amount: Yup.number().required(''),
      discount: Yup.number().required(''),
      vat: Yup.number().required(''),
      name: Yup.string().required(''),
      qty: Yup.number().integer().min(1, ' ').required(' ')
    })
  )
})

type Props = {
  order?: SupplierOperation
  setOrder: Dispatch<SetStateAction<SupplierOperation | undefined>>
  setPriceDigits: Dispatch<SetStateAction<number>>
  setContacts: Dispatch<SetStateAction<Array<string>>>
}

const BulkOrderForm : React.FC<Props> = ({order, setOrder, setPriceDigits, setContacts}) => {
  const navigate = useNavigate()
  const [suppliers, setSuppliers] = useState<Array<Supplier>>([])
  const [products, setProducts] = useState<Array<RegistredProduct>>([])
  const currentState = order?.states?.at(-1)?.state || 'INIT'
  const isMutable = ['INIT', 'UPDATED'].includes(currentState) && order?.parent === undefined
  const getSuppliers = async () => setSuppliers(await getSuppliersArray())

  const handleSupplierChange = async (supplierId: ID) => {
    const supplier = suppliers.find(s => s._id === supplierId);
    if (!supplier) return;

    setPriceDigits(supplier.priceDigits ?? 3);
    setContacts(supplier.contacts ?? []);
    setProducts(await getRegistredProducts(supplierId))
  };

  useEffect(() => {
    void (suppliers.length === 0 && getSuppliers());
    void (order  && handleSupplierChange(order.supplier));
  }, [order, suppliers]);

  if (order && order?.products) {
    order.products.map((p: OpProduct) => {
      const _ = products.find(x => x._id === p.product)
      if (_) {
        p.stock = _.stock
        p.threshold = _.threshold
      }
    })
  }

  return (
    <Formik
      initialValues={order || initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        try {
          if (order === undefined) {
            const res = await createBulkOrder(values as SupplierOperation)
            navigate(`/p2c/supplier/bulk-order/${res?._id}`)
          } else {
            const res = await updateBulkOrder(values as SupplierOperation)
            setOrder(res)
          }
        } catch (e: unknown) {
          console.log('Bulk order error', e)
        }
      }}
    >
      {({ values }) => {
        return (
          <Form>
            <div className="d-flex flex-stack">
              <Field
                component={CustomSelect}
                default="Select a supplier"
                options={suppliers.map((s: Supplier) => {
                  return {
                    value: s._id,
                    label: s.name
                  }
                })}
                name="supplier"
                id="supplier"
                required={true}
                disabled={order !== undefined}
                onChange={async (e: React.ChangeEvent<HTMLSelectElement>) => {
                  values.products = []
                  await handleSupplierChange(e.target.value as ID);
                }}
              />

              {order?.childs && order.childs.length > 0 && (
                <div className="text-danger">
                  This order is splitted 
                  {order.childs.map((child: ID, index: number) => (
                    <a
                      key={`split-bk-${index + 1}`}
                      href={`/p2c/supplier/bulk-order/${child}`}
                      className="text-danger fw-bold mx-1"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Part {index + 1}
                    </a>
                  ))}
                 
                </div>
              )}

              {order?.parent && (
                <div className="text-danger">
                  This order is a part of
                  <a
                    href={`/p2c/supplier/bulk-order/${order.parent}`}
                    className="text-danger fw-bold mx-1"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {order?.reference?.replace(/-\d+$/, '')}
                  </a>
                  delivred in two times
                </div>
              )}


              {order && (
                <div className="d-flex">
                  <h4 className="fw-bolder text-gray-700 my-auto">{order.reference}</h4>
                </div>
              )}
            </div>


            <div className="flex-grow-1">
              <FieldArray name="products">
                {({ push, remove }) => (
                  <div className="table-responsive border-bottom mb-9">
                    {values.supplier && isMutable && (
                      <Lookup push={push} products={products} selected={(values?.products || []).map(
                        (p: OpProduct) => p.product
                      )}/>
                    )}
                    <table className="table align-middle table-row-dashed">
                      <thead>
                        <tr className="border-bottom fs-6 fw-bold text-muted">
                          <th className="min-w-175px pb-2">Product</th>
                          {isMutable && (
                            <th className="w-80px d-flex align-items-center gap-1 pb-2">
                              <span className="badge badge-primary fw-semibold" title="Stock">S</span>
                              <span className="badge badge-danger fw-bold" title="Thresold">T</span>
                            </th>
                          )}
                          <th className="w-125px text-start pb-2">Qty</th>
                          <th className="min-w-80px text-center pb-2">Unit Price</th>
                          <th className="min-w-80px text-center pb-2">Discount</th>
                          <th className="min-w-100px text-center pb-2">Vat</th>
                          <th className="min-w-100px text-end pb-2">Total amount</th>

                          {isMutable && (
                            <th className="min-w-100px text-end pb-2"></th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {(values?.products || []).map((row, index) => (
                          <tr key={`row-${index}`} className="fw-bold text-gray-700 fs-5 text-end">

                            <td className="text-start">
                              <Field name={`products[${index}].name`} type="hidden" />
                              <Field name={`products[${index}].product`} type="hidden" />
                              {row.name}
                            </td>
                            {isMutable && (
                              <td className="text-center">
                                <span className="d-flex align-items-center gap-1">
                                  <span className="badge badge-primary fw-semibold">{row.stock}</span>
                                  <span className="badge badge-danger fw-bold">{row.threshold}</span>
                                </span>
                              </td>
                            )}
                            <td className="d-flex align-items-center gap-2">
                              <Field
                                name={`products[${index}].qty`}
                                component={InputText}
                                className="form-control-sm"
                                type="number"
                                placeholder="0"
                                required={true}
                                disabled={!isMutable}
                                rawInput={true}
                              />
                              <span className="my-auto" title={`${row.delivred} delivred`}>
                              {row.delivred === row.qty ? (
                                <KTIcon iconName='check-circle' className='fs-1 text-success' />
                              ) : (
                                <KTIcon iconName='delivery-time' className='fs-1 text-primary' />
                              )}
                            </span>
                            </td>
                            <td className="text-center">
                              <Field name={`products[${index}].amount`} type="hidden" />
                              {row.amount.toFixed(4)}
                            </td>
                            <td className="text-center">
                              <Field name={`products[${index}].discount`} type="hidden" />
                              {row.discount}%
                            </td>

                            <td className="text-center">
                              <Field name={`products[${index}].vat`} type="hidden" />
                              {row.vat}%
                            </td>
                            <td className="text-end">
                              { (parseFloat((row.amount * (100 - row.discount) / 100).toFixed(2)) * row.qty).toFixed(2) }
                            </td>

                            {isMutable && (
                            <td className="text-end">
                              <button
                                className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1"
                                type="button"
                                onClick={() => remove(index)}
                              >
                                <KTIcon iconName='cross' className='fs-3' />
                              </button>
                            </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </FieldArray>
              <Subtotal />
            </div>
            <Field
              component={FormActions}
              position='end'
              disabled={!isMutable}
              confirmBtnLabel={order === undefined ? 'Create order' : 'Update order'}
              discardBtnLabel='Back'
              close={()=> navigate('/p2c/supplier/operations')}
            />
          </Form>
        )
      }}
    </Formik>
  )
}

export {BulkOrderForm}
